import { computed, inject, Injectable, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Optimus } from '@openng/optimus-ui/config';
import { de } from '@openng/optimus-ui-locale/de.json';
import { it } from '@openng/optimus-ui-locale/it.json';
import { fr } from '@openng/optimus-ui-locale/fr.json';

type SupportedLanguage = 'de' | 'it' | 'fr';

export const SUPPORTED_LANGUAGES = [
  'de',
  'fr',
  'it',
] as const satisfies readonly SupportedLanguage[];

export const DEFAULT_LANGUAGE = 'de';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private optimusConfig = inject(Optimus);
  private translocoService = inject(TranslocoService);

  private language = signal<SupportedLanguage>(DEFAULT_LANGUAGE);

  currentLanguage = computed(() => this.language());

  initFromBrowser() {
    const storedLanguage = localStorage.getItem('language');
    if (storedLanguage != null) {
      this.set(storedLanguage as SupportedLanguage);
    } else {
      const lang = this.detectBrowserLanguage();
      this.set(lang);
    }
  }

  set(language: SupportedLanguage) {
    switch (language) {
      case 'de':
        this.optimusConfig.setTranslation(de);
        break;
      case 'it':
        this.optimusConfig.setTranslation(it);
        break;
      case 'fr':
        this.optimusConfig.setTranslation(fr);
        break;
      default:
        this.optimusConfig.setTranslation(de);
        break;
    }

    this.translocoService.setActiveLang(language);
    this.language.set(language);
    localStorage.setItem('language', language);
  }

  private detectBrowserLanguage(): SupportedLanguage {
    if (typeof navigator === 'undefined') return DEFAULT_LANGUAGE;
    const prefs = [
      ...(navigator.languages ?? []),
      navigator.language,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (navigator as any).browserLanguage,
    ]
      .filter(Boolean)
      .map((l: string) => l.toLowerCase());

    for (const p of prefs) {
      const base = p.split('-')[0] as SupportedLanguage;
      if (SUPPORTED_LANGUAGES.includes(base)) {
        return base;
      }
    }
    return DEFAULT_LANGUAGE;
  }
}
